import { useEffect } from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { sanitizeHTML } from 'utils/helpers';

import { getPartnerPreferences } from 'services/suggestedMessages';

import { TriggersI18nKeys } from 'components/MessageTipsDrawer/constants';
import { PreferredTemplatesPreviewProps } from 'components/MessageTipsDrawer/types';

import * as S from '../../styles';

export const PreferredTemplatesPreview: React.FC<PreferredTemplatesPreviewProps> =
  ({ triggerCode, handleNextStep, setIsErrorSnackOpen }) => {
    const intl = useIntl();

    const {
      data: partnerPreferences,
      isLoading,
      isError,
    } = useQuery({
      queryFn: () => getPartnerPreferences(),
      queryKey: ['partnerPreferences'],
    });

    useEffect(() => {
      if (isError) {
        setIsErrorSnackOpen(true);
      }
    }, [isError, setIsErrorSnackOpen]);

    const preferredMessage = Array.isArray(partnerPreferences)
      ? partnerPreferences.find(
          preference => preference.trigger_code === triggerCode,
        )?.message_templates?.[0]?.message || ''
      : '';

    if (!triggerCode || isLoading || preferredMessage === '') {
      return (
        <>
          <Skeleton
            variant="text"
            width="100%"
            height={20}
            animation="wave"
            sx={{ mt: 2 }}
          />
          <Skeleton
            variant="rounded"
            width="100%"
            height={180}
            animation="wave"
            sx={{ mt: 2 }}
          />
          <Box display="flex" justifyContent="flex-end">
            <Skeleton
              variant="rounded"
              width={100}
              height={30}
              animation="wave"
              sx={{ mt: 2 }}
            />
          </Box>
        </>
      );
    }

    return (
      <>
        <Typography variant="caption" sx={{ mt: 2 }}>
          {intl.formatMessage(
            {
              id: TriggersI18nKeys[triggerCode].description,
            },
            {
              b: (msg: string) => (
                <Typography variant="caption" component="span" fontWeight={500}>
                  {msg}
                </Typography>
              ),
            },
          )}
        </Typography>

        <S.MessagePreviewContainer data-testid="messagePreviewContainer">
          <S.MessagePreviewText
            dangerouslySetInnerHTML={sanitizeHTML(preferredMessage, {
              ALLOWED_TAGS: ['b'],
              ALLOWED_ATTR: [],
            })}
          />
        </S.MessagePreviewContainer>

        <S.NextStepButton onClick={handleNextStep}>
          <S.NextStepIcon />
          {intl.formatMessage({ id: 'MESSAGETIPS.GLOBAL.EDIT' })}
        </S.NextStepButton>
      </>
    );
  };
